import { useMutation } from "@tanstack/react-query";
import { CheckBox, Text } from "@ui-kitten/components";
import { isBigScreen } from "constants/";
import {
  AccountTicketDetails,
  TicketDetails,
  TicketProvider,
} from "features/types";
import { useAccount } from "hooks/useAccount";
import useCopyText from "hooks/useCopyText";
import useCurrentUser from "hooks/useCurrentUser";
import useResolveNativeUrl from "hooks/useResolveNativeUrl";
import useShare from "hooks/useShare";
import i18n from "plugins/localization";
import { useFetchTenantConfigurationQuery } from "queries/configuration.queries";
import { initiateTransferFetcher } from "queries/ticket.queries";
import React, { useMemo, useState } from "react";
import { ScrollView, View } from "react-native";
import { formatEventDate } from "utils/eventUtils";
import { getUserName } from "utils/userUtils";
import Dialog, { DialogProps } from "./Dialog";

interface ShareDialogProps extends DialogProps {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  title: string;
  accountEvent: AccountTicketDetails;
  path: string;
  type: "guest" | "seats";
  ticketProvider: TicketProvider;
}

const getTicketLabel = (ticket: TicketDetails): string => {
  const { seat } = ticket;
  return `Sec ${seat.sectionDisplay} · Row ${seat.rowDisplay} · Seat ${seat.seatDisplay}`;
};

const ShareDialog: React.FC<ShareDialogProps> = (props) => {
  const {
    visible,
    setVisible,
    title,
    accountEvent,
    path,
    type,
    ticketProvider,
  } = props;
  const { account } = useAccount();
  const { data: tenantConfig } = useFetchTenantConfigurationQuery();

  const transferableTickets = useMemo(() => {
    if (!account) return [];
    return accountEvent.tickets.filter((ticket) => {
      const isCorrectType =
        type === "guest"
          ? ticket.planPackageComponentType.includes("GUEST")
          : !ticket.planPackageComponentType.includes("GUEST");
      return (
        ticket.reservedByAccountId === account.id &&
        isCorrectType &&
        (ticket.status === "RESERVED" ||
          (ticket.status === "ISSUED" && ticket.allowIssuedTransfer)) &&
        ticket.transferability !== "NONE"
      );
    });
  }, [accountEvent.tickets, account, type]);

  const [selectedTicketIds, setSelectedTicketIds] = useState<Set<number>>(
    () => new Set(transferableTickets.map((t) => t.id)),
  );
  const [transferCode, setTransferCode] = useState("");
  const { currentUser } = useCurrentUser();
  const nativeUrl = useResolveNativeUrl({
    path,
    accountEvent,
    transferCode: transferCode!,
  });
  const { handleShare, copied, shareError } = useShare();
  const { copyText, copied: codeCopied } = useCopyText();
  const { mutateAsync: initiateTransferRequest, isLoading } = useMutation(
    initiateTransferFetcher,
  );

  const message = useMemo(() => {
    if (!currentUser) return "";
    const {
      event: { name },
    } = accountEvent!;
    const eventDate = formatEventDate(accountEvent!.event);
    const userName = getUserName(currentUser!);
    return `${userName} has sent you a ticket for ${name} on ${eventDate}. To claim the ticket, click the link below. ${nativeUrl}`;
  }, [currentUser, nativeUrl]);

  const allSelected = selectedTicketIds.size === transferableTickets.length;

  const toggleTicket = (ticketId: number) => {
    setSelectedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) {
        next.delete(ticketId);
      } else {
        next.add(ticketId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedTicketIds(new Set());
    } else {
      setSelectedTicketIds(new Set(transferableTickets.map((t) => t.id)));
    }
  };

  const findGuestComponentType = () => {
    const guestTicket = accountEvent.tickets.find((ticket) =>
      ticket.planPackageComponentType.includes("GUEST"),
    );
    if (!guestTicket) {
      throw new Error("No guest component ticket found");
    }
    return guestTicket.planPackageComponentType;
  };

  const findSeatComponentType = () => {
    const seat = accountEvent.tickets.find(
      (ticket) => !ticket.planPackageComponentType.includes("GUEST"),
    );
    if (!seat) {
      throw new Error("No seat component ticket found");
    }
    return seat.planPackageComponentType;
  };

  const initiateTransfer = async () => {
    if (!account || selectedTicketIds.size === 0) return;

    const result = await initiateTransferRequest({
      accountId: account.id,
      ticketIds: Array.from(selectedTicketIds),
      groupCodeId: accountEvent!.groupCode.id,
      planPackageComponentType:
        type === "guest" ? findGuestComponentType() : findSeatComponentType(),
    });
    setTransferCode(result.transferCode);
  };

  // https://github.com/FanRally/fanrally-core/issues/1226
  // If ticket provider is AXS, do not allow in-app ticket transfer/invite guests
  if (ticketProvider === "AXS") {
    const tenantAlias = accountEvent.tickets[0].event.tenantAlias;
    const title = type === "guest" ? "Guest Passes" : "Seats";
    const appName = tenantConfig?.mobileAppName || tenantAlias;
    const axsAccountName = account?.extUserDetails?.axsUsername || "N/A";

    return (
      <Dialog
        visible={visible}
        setVisible={setVisible}
        cancelText={i18n.t("common.close")}
      >
        <Text category="h6">{title}</Text>
        <Text style={{ marginVertical: 10 }}>
          {title} may be transferred from within the {appName} app in the
          following AXS account: {axsAccountName}.
        </Text>
        <Text status="danger">
          NOTE: Once transferred, this booking is final.
        </Text>
      </Dialog>
    );
  }

  return (
    <Dialog
      visible={visible}
      setVisible={setVisible}
      cancelText="Close"
      okText={
        transferCode
          ? isBigScreen
            ? codeCopied
              ? "Copied"
              : "Copy"
            : copied
              ? "Copied"
              : "Share"
          : "Get Code"
      }
      onOk={() => {
        if (!transferCode) {
          initiateTransfer();
        } else if (isBigScreen) {
          copyText(message);
        } else {
          handleShare(message);
        }
      }}
      okBtnProps={{
        status: "info",
        loading: isLoading,
        disabled: selectedTicketIds.size === 0,
      }}
      style={{
        width: 375,
      }}
    >
      <Text category="h6">{title}</Text>
      {!transferCode && (
        <>
          {transferableTickets.length === 0 ? (
            <Text status="danger">No remaining transferrable tickets</Text>
          ) : (
            <View style={{ marginVertical: 10 }}>
              <CheckBox
                checked={allSelected}
                indeterminate={selectedTicketIds.size > 0 && !allSelected}
                onChange={toggleAll}
                style={{ marginBottom: 8 }}
              >
                Select All
              </CheckBox>
              <ScrollView style={{ maxHeight: 200 }}>
                {transferableTickets.map((ticket) => (
                  <CheckBox
                    key={ticket.id}
                    checked={selectedTicketIds.has(ticket.id)}
                    onChange={() => toggleTicket(ticket.id)}
                    style={{ marginVertical: 4 }}
                  >
                    {getTicketLabel(ticket)}
                  </CheckBox>
                ))}
              </ScrollView>
            </View>
          )}
        </>
      )}

      {!!transferCode && (
        <>
          <Text style={{ marginTop: 10 }}>Your Guest Invitation Code:</Text>
          <Text category="s1" style={{ marginVertical: 5 }}>
            {transferCode}
          </Text>
          {isBigScreen ? (
            <Text>
              Click COPY to copy your transfer code to your clipboard, then
              paste into a text or email to send to a recipient!
            </Text>
          ) : (
            <>
              <Text>Click Share to share this code with your guests</Text>
              {shareError && (
                <>
                  <Text status="info">Share not supported on this device</Text>
                  <Text>{nativeUrl}</Text>
                </>
              )}
            </>
          )}
        </>
      )}
    </Dialog>
  );
};

export default ShareDialog;
